import type { UnifiedRule } from '@/services/core/BusinessRulesService';
import { normalizeActionsForEditor } from '@/core/rules/actionsJson';
import { isOnTransition, onTransitionTrigger, parseTrigger } from '@/core/rules/triggerScope';

/**
 * The create-form pre-fill for an override of a system rule — the ONE builder both the
 * Business Rules grid and the system-rule page use. The `when` AST alone does not say what a
 * null means — C1's nullRelationBehavior and C2's nullFieldBehavior do, and an override
 * regenerates its expression from `when` WITH those knobs — so they travel with it, or an
 * override of SYS-2004 would silently turn "no day count is nothing to check" back into
 * default-fail. C8: the severity and message travel too, so an advisory stays advisory.
 * C18: the override inherits the parent's transition scope as its own (see overrideTrigger).
 */
export function buildOverridePreFill(rule: UnifiedRule): Record<string, unknown> {
  return withoutUndefined({
    ruleKey: rule.ruleKey,
    module: rule.module,
    entity: rule.entity,
    name: `${rule.name} (Override)`,
    description: rule.description,
    priority: (rule.priority || 100) + 1000,
    isOverride: true,
    trigger: overrideTrigger(rule),
    when: rule.when,
    actions: actionsForEditor(rule.actions),
    nullRelationBehavior: rule.nullRelationBehavior,
    nullFieldBehavior: rule.nullFieldBehavior,
    errorMessage: rule.errorMessage,
    errorType: rule.errorType,
  });
}

/**
 * The create form merges the pre-fill OVER the schema defaults, and an own key holding
 * `undefined` wins that merge — a parent rule that declares no null-handling knob would blank
 * the "fail" default the form is supposed to show. Absent keys let the default stand.
 */
function withoutUndefined(prefill: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(prefill).filter(([, value]) => value !== undefined));
}

/**
 * A system rule's OnTransition trigger names no transitions itself — its scope lives in the
 * state machine's `transitions[].rules` and reaches the UI as `rule.transitions`. The
 * override must carry that scope explicitly: the save refuses a blank scope (X1), and an
 * override authored with a different scope would silently run elsewhere than its parent.
 */
export function overrideTrigger(rule: UnifiedRule): string | undefined {
  const parent = parseTrigger(rule.trigger);
  if (!parent || !isOnTransition(parent)) {
    return rule.trigger ?? undefined;
  }
  const inherited = (rule.transitions ?? []).map(({ from, to }) => ({ from, to }));
  return JSON.stringify(onTransitionTrigger(inherited));
}

/**
 * The actions a system rule seeds are stored in the seeder's PascalCase shape
 * ({ Type, Parameters }); the rule editor reads the camelCase shape it writes
 * ({ type, parameters }). The pre-fill hands the editor its own shape, or the copied
 * ValidationError action arrives as an empty "Action 1".
 */
function actionsForEditor(actions?: string | null): string | undefined {
  return actions ? normalizeActionsForEditor(actions) : undefined;
}
